// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Torc — the solo opponent. In a deep abstract the bot IS the product for one player, so this is a
// real engine: negamax with alpha-beta, iterative deepening, a time budget, and an evaluation built
// out of the thing the game is actually about.
//
// THREE THINGS ABOUT THIS GAME MAKE THE SEARCH UNUSUAL, and each one is handled explicitly:
//
// 1. A TURN IS NOT A PLY. Riding a ring can hand you a line to claim — and can hand the OPPONENT
//    one, on your turn — so the side to move does not simply alternate. negamax() therefore asks the
//    child whose turn it is and only negates when the side actually changed. Getting this wrong does
//    not crash; it silently makes the bot evaluate its own claims as the opponent's.
// 2. CLAIMS DO NOT COST DEPTH. A claim is a near-forced continuation with at most a dozen options,
//    and stopping the search on an unresolved claim would read a won line as an ordinary position.
//    Claim plies recurse at the same depth. It terminates because every claim burns one of a finite
//    number of rings.
// 3. THE OPENING IS A PLACEMENT PHASE WITH A 31–85 WIDE BRANCH. Searching that raw is hopeless, so
//    placements are scored by a heuristic (centre, spread, room to ride) and only the best few are
//    handed to the search. That is the same two-stage narrowing the ride phase uses, just with a
//    different first stage.
//
// The whole search is a pure synchronous function with a deadline, so tests/balance.test.ts drives
// the SHIPPED engine — budgeted by node count rather than a wall clock, which is what makes the
// simulation reproducible on any machine. No requestAnimationFrame anywhere: a backgrounded tab
// throttles it to nothing, and an opponent that cannot finish its turn is a hang, not a delay.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import {
  AXES,
  EMPTY,
  PLY_CLAIM,
  applyPly,
  cloneState,
  copyInto,
  countPlies,
  genPlies,
  isOver,
  isMark,
  markOf,
  packMove,
  packPlace,
  plyA,
  plyB,
  plyKind,
  rideTargets,
  ringsInto,
  type Geom,
  type State,
} from './rules';

export interface Tier {
  id: string;
  name: string;
  blurb: string;
  depth: number;
  timeMs: number;
  /** Ride candidates kept per node. */
  width: number;
  /** Placement candidates kept per node. */
  placeWidth: number;
  /** Novice: no line-potential term at all, so it never sets a line up. */
  flat: boolean;
  noise: number;
}

export const TIERS: Tier[] = [
  {
    id: 'tinker',
    name: 'Tinker',
    blurb: 'Grabs what is in front of her and flips what she can — but never builds towards a line.',
    depth: 1,
    timeMs: 200,
    width: 6,
    placeWidth: 6,
    flat: true,
    noise: 30,
  },
  {
    id: 'smith',
    name: 'Smith',
    blurb: 'Reads a few plies ahead, sets lines up, and will jump yours apart before you close it.',
    depth: 4,
    timeMs: 600,
    width: 8,
    placeWidth: 8,
    flat: false,
    noise: 6,
  },
  {
    id: 'torcmaker',
    name: 'Torcmaker',
    blurb: 'Searches deep, counts every line on the board, and knows exactly which torc she can spare.',
    // MEASURED, not guessed. Two numbers here came out of the harness rather than a hunch:
    //
    // WIDTH 8 IS DEEPER THAN NO WIDTH AT ALL. On the 85-point field with a 400ms budget, keeping the
    // best 8 candidates reaches depth 6; keeping 12 reaches 5; keeping everything reaches 3. The
    // narrowing is not a concession to speed, it is what buys the extra plies — so do not "improve"
    // this by widening it without re-running the harness.
    //
    // THE DEPTH CAP IS DELIBERATELY HIGHER THAN THE SEARCH WILL EVER REACH, so the TIME budget is
    // the thing that actually binds and the tier behaves the same on a fast laptop and a slow phone
    // — it just thinks less deeply on the phone. An earlier cut capped depth at 5, which this
    // machine finished in a fraction of the budget: the tier was leaving its own strength unused.
    depth: 8,
    timeMs: 1400,
    width: 8,
    placeWidth: 10,
    flat: false,
    noise: 0,
  },
];

export const DEFAULT_TIER = 'smith';

export function tierOf(id: string | null | undefined): Tier {
  if (typeof id === 'string') {
    const t = TIERS.find((x) => x.id === id);
    if (t) return t;
  }
  return TIERS[1];
}

const MATE = 1_000_000;

export interface SearchOpts {
  depth: number;
  /** Wall-clock cap in ms. 0 disables it — which is what the simulation uses. */
  timeMs?: number;
  /** Hard node cap, so a simulated run is bounded regardless of machine speed. */
  nodes?: number;
  width?: number;
  placeWidth?: number;
  flat?: boolean;
  noise?: number;
  rng?: Rng;
}

export interface SearchResult {
  ply: number;
  score: number;
  depth: number;
  nodes: number;
  ms: number;
}

// ── the line-potential table ────────────────────────────────────────────────────
/**
 * Every window of `runLen` consecutive points on every line. A window holding only my markers (and
 * gaps) is a line I might finish; one holding both colours is dead to both of us. This is the whole
 * positional idea of the game, and it is why the evaluation is a window scan rather than a flood.
 */
interface Windows {
  k: number;
  count: number;
  pts: Int32Array;
}

const windowCache = new Map<string, Windows>();

export function windowsFor(g: Geom, runLen: number): Windows {
  const key = `${g.R}:${runLen}`;
  const hit = windowCache.get(key);
  if (hit) return hit;
  const pts: number[] = [];
  let count = 0;
  for (const line of g.lines) {
    for (let start = 0; start + runLen <= line.length; start++) {
      for (let k = 0; k < runLen; k++) pts.push(line[start + k]);
      count++;
    }
  }
  const w: Windows = { k: runLen, count, pts: Int32Array.from(pts) };
  windowCache.set(key, w);
  return w;
}

/** How much a partial line is worth, by how many of its points I already hold. Cubic, so three in a
 *  window is worth far more than three separate pairs — which is what "building" means here. */
const POT = [0, 1, 8, 27, 64, 125, 216];

const W_CLAIM = 1200;
/** A pending, not-yet-taken claim is worth almost the same as a taken one. Without this the search
 *  can end on an unresolved claim and read a won line as an ordinary position. */
const W_PENDING = 1000;
const W_RING = 55;
const W_POT = 4;
const W_MARKER = 6;
const W_MOB = 2;
const W_CENTRE = 3;

const centreOf = (g: Geom): Float32Array => {
  const key = `c${g.R}`;
  const cached = centreCache.get(key);
  if (cached) return cached;
  const out = new Float32Array(g.n);
  for (let i = 0; i < g.n; i++) {
    const d = (Math.abs(g.q[i]) + Math.abs(g.r[i]) + Math.abs(g.q[i] + g.r[i])) / 2;
    out[i] = 1 - d / g.R;
  }
  centreCache.set(key, out);
  return out;
};
const centreCache = new Map<string, Float32Array>();

const mobScratch: number[] = [];
const ringScratch: number[] = [];

function sideMobility(s: State, side: number): number {
  let n = 0;
  for (const at of ringsInto(s, side, ringScratch)) {
    mobScratch.length = 0;
    rideTargets(s, at, mobScratch);
    n += mobScratch.length;
  }
  return n;
}

/**
 * Score the position for `me`. Positive is good for `me`.
 *
 * The terms, in the order they matter: claims already banked (this is literally the win condition),
 * a claim sitting unresolved, the rings still on the board (a claim costs one, and the search has to
 * feel that cost or it will happily trade its last ring for a point it cannot use), line potential,
 * marker majority, ring mobility, and a small pull toward the middle where the lines are longest.
 */
export function evaluate(s: State, me: number, flat = false): number {
  const them = 1 - me;
  let score = (s.claimed[me] - s.claimed[them]) * W_CLAIM;

  if (s.phase === 'claim') score += (s.turn === me ? 1 : -1) * W_PENDING;

  let ringsMe = 0;
  let ringsThem = 0;
  let markMe = 0;
  let markThem = 0;
  let centre = 0;
  const centres = centreOf(s.g);
  const mineMark = markOf(me);
  const theirMark = markOf(them);
  for (let i = 0; i < s.cell.length; i++) {
    const v = s.cell[i];
    if (v === EMPTY) continue;
    if (v === mineMark) {
      markMe++;
      centre += centres[i];
    } else if (v === theirMark) {
      markThem++;
      centre -= centres[i];
    } else if (v === (me === 0 ? 3 : 4)) ringsMe++;
    else ringsThem++;
  }

  score += (ringsMe - ringsThem) * W_RING;
  score += (markMe - markThem) * W_MARKER;
  score += Math.round(centre * W_CENTRE);
  score += (sideMobility(s, me) - sideMobility(s, them)) * W_MOB;

  if (flat) return score;

  const win = windowsFor(s.g, s.runLen);
  const k = win.k;
  let pot = 0;
  for (let w = 0; w < win.count; w++) {
    const base = w * k;
    let a = 0;
    let b = 0;
    for (let j = 0; j < k; j++) {
      const v = s.cell[win.pts[base + j]];
      if (v === mineMark) a++;
      else if (v === theirMark) b++;
    }
    if (a && !b) pot += POT[a];
    else if (b && !a) pot -= POT[b];
  }
  return score + pot * W_POT;
}

// ── candidate generation ────────────────────────────────────────────────────────
interface Ctx {
  pool: State[];
  nodes: number;
  deadline: number;
  maxNodes: number;
  aborted: boolean;
  flat: boolean;
  width: number;
  placeWidth: number;
}

/** Is there a claimable line for `side` running through this point? Only the three lines through it
 *  are scanned, which is what makes it cheap enough for the candidate filter. */
function runThrough(s: State, at: number, side: number): boolean {
  const g = s.g;
  const want = markOf(side);
  for (let axis = 0; axis < AXES; axis++) {
    const li = g.lineOf[at * AXES + axis];
    if (li < 0) continue;
    const line = g.lines[li];
    let run = 0;
    for (let i = 0; i < line.length; i++) {
      run = s.cell[line[i]] === want ? run + 1 : 0;
      if (run >= s.runLen) return true;
    }
  }
  return false;
}

const crossScratch: number[] = [];

/** The markers a ride would cross, appended to `out`. They all flip — including your own. */
function crossedBy(s: State, from: number, to: number, out: number[]): void {
  out.length = 0;
  const g = s.g;
  const dir = g.dirOf[from * g.n + to];
  if (dir < 0) return;
  for (let cur = g.nbr[from * 6 + dir]; cur >= 0 && cur !== to; cur = g.nbr[cur * 6 + dir]) {
    if (isMark(s.cell[cur])) out.push(cur);
  }
}

const dests: number[] = [];
/** A SECOND destination buffer, and not a micro-optimisation: the ride loop iterates `dests` while
 *  asking how much room each landing point would leave, which needs its own array. Reusing one
 *  buffer there silently rewrites the list mid-iteration and emits plies that were never legal. */
const landRoom: number[] = [];
const cands: Array<{ ply: number; score: number }> = [];

/**
 * The narrowed candidate list, and the reason the search reaches a useful depth at all.
 *
 * A mid-game ride phase offers 60–150 plies. Scoring all of them with the full window evaluation
 * would spend the entire budget one ply deep. So candidates are priced with a cheap surrogate — what
 * the ride flips, whether it closes a line, whether it hands one to the opponent, and how much room
 * the ring lands with — and the real evaluation is spent only at the leaves.
 *
 * The flip term is signed on purpose: a jump flips EVERY marker it crosses, your own included, so
 * crossing your own line is how you hand it to the other player. A bot that treated flips as
 * unconditionally good would do exactly that.
 */
function candidatePlies(s: State, ctx: Ctx): number[] {
  if (s.phase === 'claim') return genPlies(s);

  const g = s.g;
  const centres = centreOf(g);
  const me = s.turn;
  const them = 1 - me;
  const mineMark = markOf(me);
  cands.length = 0;

  if (s.phase === 'place') {
    // Placement is heuristic, not searched: centre (more and longer lines run through it), spread
    // (rings on top of each other cannot build two lines), and room to ride on the first turn.
    const mine = ringsInto(s, me, ringScratch);
    for (let i = 0; i < s.cell.length; i++) {
      if (s.cell[i] !== EMPTY) continue;
      let spread = 3;
      for (const other of mine) {
        const dq = g.q[i] - g.q[other];
        const dr = g.r[i] - g.r[other];
        const d = (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
        if (d < spread) spread = d;
      }
      dests.length = 0;
      rideTargets(s, i, dests);
      cands.push({ ply: packPlace(i), score: centres[i] * 40 + Math.min(spread, 3) * 9 + dests.length });
    }
    cands.sort((a, b) => b.score - a.score);
    return cands.slice(0, Math.max(1, ctx.placeWidth)).map((c) => c.ply);
  }

  for (const from of ringsInto(s, me, ringScratch)) {
    dests.length = 0;
    rideTargets(s, from, dests);
    for (const to of dests) {
      crossedBy(s, from, to, crossScratch);
      let flip = 0;
      for (const p of crossScratch) flip += s.cell[p] === mineMark ? -1 : 1;

      // Apply in place, ask the two questions that decide most of the ordering, put it back.
      const savedFrom = s.cell[from];
      const savedTo = s.cell[to];
      s.cell[from] = mineMark;
      s.cell[to] = EMPTY;
      for (const p of crossScratch) s.cell[p] = s.cell[p] === 1 ? 2 : 1;
      let tactical = 0;
      if (runThrough(s, from, me)) tactical += 9000;
      else for (const p of crossScratch) if (runThrough(s, p, me)) { tactical += 9000; break; }
      for (const p of crossScratch) if (runThrough(s, p, them)) { tactical -= 5200; break; }
      for (const p of crossScratch) s.cell[p] = s.cell[p] === 1 ? 2 : 1;
      s.cell[from] = savedFrom;
      s.cell[to] = savedTo;

      landRoom.length = 0;
      rideTargets(s, to, landRoom);
      cands.push({
        ply: packMove(from, to),
        score: tactical + flip * 90 + landRoom.length * 3 + centres[to] * 12,
      });
    }
  }
  if (!cands.length) return [];
  cands.sort((a, b) => b.score - a.score);
  return cands.slice(0, Math.max(1, ctx.width)).map((c) => c.ply);
}

// ── the search ──────────────────────────────────────────────────────────────────

function terminal(s: State, me: number, dist: number): number {
  if (s.reason === 'draw') return 0;
  return s.winner === me ? MATE - dist : -MATE + dist;
}

function negamax(ctx: Ctx, s: State, depth: number, alpha: number, beta: number, dist: number): number {
  if ((ctx.nodes & 31) === 0) {
    if (ctx.deadline > 0 && Date.now() > ctx.deadline) ctx.aborted = true;
    if (ctx.maxNodes > 0 && ctx.nodes > ctx.maxNodes) ctx.aborted = true;
  }
  if (ctx.aborted) return 0;
  ctx.nodes++;

  if (isOver(s)) return terminal(s, s.turn, dist);
  if (depth <= 0) return evaluate(s, s.turn, ctx.flat);

  const plies = candidatePlies(s, ctx);
  if (!plies.length) return -MATE + dist;

  const me = s.turn;
  let best = -Infinity;
  const child = ctx.pool[Math.min(dist, ctx.pool.length - 1)];
  for (const ply of plies) {
    copyInto(child, s);
    if (!applyPly(child, ply, undefined, false)) continue;
    // A claim is a continuation of the same turn, not a reply to it — so it neither flips the sign
    // nor costs depth. See the file header.
    const next = plyKind(ply) === PLY_CLAIM ? depth : depth - 1;
    const score = isOver(child)
      ? terminal(child, me, dist)
      : child.turn === me
        ? negamax(ctx, child, next, alpha, beta, dist + 1)
        : -negamax(ctx, child, next, -beta, -alpha, dist + 1);
    if (ctx.aborted) return 0;
    if (score > best) best = score;
    if (best > alpha) alpha = best;
    if (alpha >= beta) break;
  }
  return best === -Infinity ? evaluate(s, me, ctx.flat) : best;
}

function makeCtx(s: State, opts: SearchOpts, started: number, maxDepth: number): Ctx {
  return {
    pool: Array.from({ length: maxDepth + 14 }, () => cloneState(s)),
    nodes: 0,
    deadline: opts.timeMs && opts.timeMs > 0 ? started + opts.timeMs : 0,
    maxNodes: opts.nodes ?? 0,
    aborted: false,
    flat: !!opts.flat,
    width: opts.width ?? 10,
    placeWidth: opts.placeWidth ?? 8,
  };
}

/** Iterative deepening from the root. Returns the best ply found inside the budget. */
export function searchTurn(s: State, opts: SearchOpts): SearchResult {
  const started = Date.now();
  const maxDepth = Math.max(1, opts.depth);
  const ctx = makeCtx(s, opts, started, maxDepth);

  const root = candidatePlies(s, ctx);
  if (!root.length) return { ply: -1, score: -MATE, depth: 0, nodes: 0, ms: 0 };

  const noise = opts.noise ?? 0;
  const rng = opts.rng ?? makeRng((s.seed ^ Math.imul(s.ply, 2654435761)) >>> 0);
  const me = s.turn;

  let bestPly = root[0];
  let bestScore = -Infinity;
  let reached = 0;

  for (let depth = 1; depth <= maxDepth; depth++) {
    let localBest = -Infinity;
    let localPly = bestPly;
    let alpha = -Infinity;
    const child = ctx.pool[0];

    // Search the previous iteration's best first — that is what makes deepening pay for itself.
    const ordered = [bestPly, ...root.filter((p) => p !== bestPly)];
    for (const ply of ordered) {
      copyInto(child, s);
      if (!applyPly(child, ply, undefined, false)) continue;
      const next = plyKind(ply) === PLY_CLAIM ? depth : depth - 1;
      let score = isOver(child)
        ? terminal(child, me, 1)
        : child.turn === me
          ? negamax(ctx, child, next, alpha, Infinity, 1)
          : -negamax(ctx, child, next, -Infinity, -alpha, 1);
      if (ctx.aborted) break;
      // A small seeded jitter, always on: without it every game against a given tier from a given
      // position is byte-identical, and a perfect-information game with no board randomness would
      // otherwise replay the same opening every single time.
      score += (rng() - 0.5) * 2 * (noise + 1.5);
      if (score > localBest) {
        localBest = score;
        localPly = ply;
      }
      if (score > alpha) alpha = score;
    }
    if (ctx.aborted) break;
    bestPly = localPly;
    bestScore = localBest;
    reached = depth;
    if (bestScore >= MATE - 32) break;
  }

  return { ply: bestPly, score: bestScore, depth: reached, nodes: ctx.nodes, ms: Date.now() - started };
}

export function optsForTier(t: Tier, seed = 0): SearchOpts {
  return {
    depth: t.depth,
    timeMs: t.timeMs,
    width: t.width,
    placeWidth: t.placeWidth,
    flat: t.flat,
    noise: t.noise,
    rng: makeRng((seed ^ 0x9e37) >>> 0),
  };
}

/**
 * A one-ply control arm for the balance simulation: take whatever flips the most and lands with the
 * most room, with no lookahead and no idea what a line is. Deliberately NOT the Tinker tier — a
 * control the simulation compares against must be a different animal, or "the engine beats the
 * control" is only the engine beating itself.
 */
export function greedyPly(s: State, rng: Rng): number {
  const plies = genPlies(s);
  if (!plies.length) return -1;
  const g = s.g;
  const centres = centreOf(g);
  const me = s.turn;
  const mineMark = markOf(me);
  let best = -1;
  let bestScore = -Infinity;
  for (const ply of plies) {
    let score = rng() * 4;
    if (plyKind(ply) === PLY_CLAIM) {
      best = ply;
      break;
    }
    if (s.phase === 'place') {
      score += centres[plyA(ply)] * 30;
    } else {
      const from = plyA(ply);
      const to = plyB(ply);
      crossedBy(s, from, to, crossScratch);
      let flip = 0;
      for (const p of crossScratch) flip += s.cell[p] === mineMark ? -1 : 1;
      landRoom.length = 0;
      rideTargets(s, to, landRoom);
      score += flip * 40 + landRoom.length * 2 + centres[to] * 8;
    }
    if (score > bestScore) {
      bestScore = score;
      best = ply;
    }
  }
  return best;
}

export function randomPly(s: State, rng: Rng): number {
  const plies = genPlies(s);
  if (!plies.length) return -1;
  return plies[Math.floor(rng() * plies.length)];
}

/** The one entry point the game screen uses. */
export function chooseAny(s: State, opts: SearchOpts): number {
  if (isOver(s) || countPlies(s) === 0) return -1;
  return searchTurn(s, opts).ply;
}
